<template>
  <div class="mensageria-stats-panel">
    <h5 class="mb-3">Estatísticas</h5>

    <div v-if="loading" class="text-muted">
      <i class="fas fa-spinner fa-spin mr-1"></i>
      Carregando estatísticas...
    </div>

    <base-alert v-else-if="error" type="warning" class="w-100">
      {{ error }}
    </base-alert>

    <template v-else>
      <div class="stat-tiles mb-3">
        <div v-for="card in totalCards" :key="card.key" class="stat-tile">
          <div class="stat-tile-label">{{ card.label }}</div>
          <div class="stat-tile-value">{{ card.value }}</div>
        </div>
      </div>

      <h6 class="text-muted text-uppercase small mb-2">Por modelo</h6>
      <div v-if="!byModel.length" class="text-muted small mb-4">
        Nenhum disparo no período.
      </div>
      <div v-else class="table-responsive mb-4">
        <table class="table table-sm table-striped mb-0">
          <thead class="thead-light">
            <tr>
              <th>Modelo</th>
              <th class="text-right">Agendadas</th>
              <th class="text-right">Disparo imediato</th>
              <th class="text-right">Falhas</th>
              <th class="text-right">Destinatário inválido</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in byModel" :key="row.model_name || row.modelName">
              <td>
                <code class="small">{{
                  row.model_name || row.modelName || '—'
                }}</code>
              </td>
              <td class="text-right">{{ row.scheduled || 0 }}</td>
              <td class="text-right">{{ row.immediate || 0 }}</td>
              <td class="text-right">{{ row.failed || 0 }}</td>
              <td class="text-right">
                {{ row.invalid_recipient || row.invalidRecipient || 0 }}
              </td>
              <td class="text-right">{{ modelTotal(row) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h6 class="text-muted text-uppercase small mb-2">
        Por horário
        <span class="hour-tz-hint font-weight-normal"
          >(enviados · status 1 · fuso local)</span
        >
      </h6>
      <div v-if="!byHour.length" class="text-muted small">
        Sem dados de horário no período.
      </div>
      <template v-else>
        <div class="hour-legend small mb-2">
          <span class="hour-legend-item">
            <span class="hour-swatch hour-swatch--immediate"></span>
            Disp. imediato
          </span>
          <span class="hour-legend-item">
            <span class="hour-swatch hour-swatch--scheduled"></span>
            Disp. agendado
          </span>
        </div>
        <ul class="list-unstyled hour-bars mb-0">
          <li
            v-for="slot in byHourNormalized"
            :key="slot.hour"
            class="hour-bar-row"
          >
            <span class="hour-label">{{ formatHour(slot.hour) }}</span>
            <div class="hour-track">
              <div
                class="hour-fill hour-fill--immediate"
                :style="{ width: slot.immediatePercent + '%' }"
                :title="'Imediato: ' + slot.immediate"
              />
              <div
                class="hour-fill hour-fill--scheduled"
                :style="{ width: slot.scheduledPercent + '%' }"
                :title="'Agendadas: ' + slot.scheduled"
              />
            </div>
            <span class="hour-count" :title="hourTitle(slot)">{{
              slot.total
            }}</span>
          </li>
        </ul>
      </template>
    </template>
  </div>
</template>

<script>
import { utcHourToLocalHour } from './utils/datetime'

export default {
  name: 'MensageriaStatsPanel',
  props: {
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    stats: { type: Object, default: null },
  },
  computed: {
    totals() {
      return (this.stats && this.stats.totals) || {}
    },
    byModel() {
      const rows =
        (this.stats && (this.stats.by_model || this.stats.byModel)) || []
      return Array.isArray(rows) ? rows : []
    },
    byHour() {
      const rows =
        (this.stats && (this.stats.by_hour || this.stats.byHour)) || []
      return Array.isArray(rows) ? rows : []
    },
    /**
     * Cards alinhados à API (status=1 + timing para Agendadas/imediato):
     * - Disparos: totals.total (status=1)
     * - Total: completed_or_failed (1, -9, 2)
     * - Agendadas: scheduled (status=1 e create→send ≥ 1 min)
     * - Disparo imediato: immediate (status=1 e mesmo minuto)
     * - Falhas / Destinatário inválido: -9 / -8
     */
    totalCards() {
      const t = this.totals
      return [
        {
          key: 'disparos',
          label: 'Disparos',
          value: t.total != null ? t.total : t.sent || 0,
        },
        {
          key: 'total',
          label: 'Total',
          value: this.completedOrFailedTotal(t),
        },
        { key: 'scheduled', label: 'Agendadas', value: t.scheduled || 0 },
        {
          key: 'immediate',
          label: 'Disparo imediato',
          value: t.immediate || 0,
        },
        { key: 'failed', label: 'Falhas', value: t.failed || 0 },
        {
          key: 'invalid_recipient',
          label: 'Destinatário inválido',
          value: t.invalid_recipient || t.invalidRecipient || 0,
        },
      ]
    },
    /**
     * by_hour API: status==1 com séries immediate + scheduled (+ total).
     * Remapeia UTC→local; barras empilhadas (total = soma das séries).
     */
    byHourNormalized() {
      const map = {}
      for (let h = 0; h < 24; h += 1) {
        map[h] = { immediate: 0, scheduled: 0 }
      }
      this.byHour.forEach((row) => {
        const utcHour = Number(row.hour)
        if (utcHour < 0 || utcHour > 23) return
        const localHour = utcHourToLocalHour(utcHour)
        const immediate = Number(row.immediate) || 0
        const scheduled = Number(row.scheduled) || 0
        if (row.immediate != null || row.scheduled != null) {
          map[localHour].immediate += immediate
          map[localHour].scheduled += scheduled
        } else {
          // fallback legado: só total
          map[localHour].immediate +=
            Number(row.total != null ? row.total : row.count) || 0
        }
      })
      const totals = Object.keys(map).map((hour) => {
        const immediate = map[hour].immediate
        const scheduled = map[hour].scheduled
        return immediate + scheduled
      })
      const max = Math.max(1, ...totals)
      return Object.keys(map).map((hour) => {
        const immediate = map[hour].immediate
        const scheduled = map[hour].scheduled
        const total = immediate + scheduled
        return {
          hour: Number(hour),
          immediate,
          scheduled,
          total,
          immediatePercent: Math.round((immediate / max) * 100),
          scheduledPercent: Math.round((scheduled / max) * 100),
        }
      })
    },
  },
  methods: {
    /** Card Total / by_model.total: status IN (1, -9, 2). */
    completedOrFailedTotal(row) {
      const r = row || {}
      if (r.completed_or_failed != null || r.completedOrFailed != null) {
        return Number(r.completed_or_failed || r.completedOrFailed) || 0
      }
      // by_model já manda `total` = 1|-9|2
      if (r.total != null && (r.scheduled != null || r.immediate != null)) {
        return Number(r.total) || 0
      }
      return (
        (Number(r.sent) || 0) + (Number(r.failed) || 0) + (Number(r.queued) || 0)
      )
    },
    modelTotal(row) {
      return this.completedOrFailedTotal(row)
    },
    formatHour(hour) {
      const h = String(hour).padStart(2, '0')
      return `${h}h`
    },
    hourTitle(slot) {
      if (!slot) return ''
      return (
        'Imediato: ' +
        slot.immediate +
        ' · Agendadas: ' +
        slot.scheduled +
        ' · Total: ' +
        slot.total
      )
    },
  },
}
</script>

<style scoped>
.stat-tiles {
  display: grid;
  gap: 0.5rem;
  /* 11rem cabe "Destinatário inválido" numa linha; abaixo disso vira 1 coluna. */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 11rem), 1fr));
}

.stat-tile {
  background: #f7fafc;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 0.75rem 0.9rem;
  min-width: 0;
}

.stat-tile-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8898aa;
  white-space: nowrap;
}

.stat-tile-value {
  font-size: 1.35rem;
  font-weight: 600;
  color: #12576e;
  line-height: 1.2;
}

.hour-tz-hint {
  text-transform: none;
  letter-spacing: normal;
  color: #adb5bd;
}

.hour-bar-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
}

.hour-label {
  width: 2.5rem;
  color: #8898aa;
  flex-shrink: 0;
}

.hour-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  color: #8898aa;
}

.hour-legend-item {
  display: inline-flex;
  align-items: center;
}

.hour-swatch {
  display: inline-block;
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 2px;
  margin-right: 0.35rem;
}

.hour-swatch--immediate,
.hour-fill--immediate {
  background: #12576e;
}

.hour-swatch--scheduled,
.hour-fill--scheduled {
  background: #2dce89;
}

.hour-track {
  flex: 1;
  height: 0.55rem;
  background: #eef2f5;
  border-radius: 2px;
  overflow: hidden;
  margin: 0 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.hour-fill {
  height: 100%;
  min-width: 0;
}

.hour-count {
  width: 2.5rem;
  text-align: right;
  color: #525f7f;
  flex-shrink: 0;
}
</style>
